import isEqual from 'lodash/isEqual';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type { CurrencyResponse, EstimationModelResponse } from '@/api/schema/data-contracts';
import { sortResourceTypes } from '@/utils/sorting';

import type { CostModel } from '../../model';
import { CostPerUnitTable } from './CostPerUnitTable';
import type { CostPerUnitFormModel } from './model';

export interface CostPerUnitForm {
  estimationCost: CostModel | undefined;
  currencies: CurrencyResponse[];
  estimationModel: EstimationModelResponse;
  resourceDisplayNames?: Record<string, string>;
  onSubmit: (formModel: CostPerUnitFormModel) => void;
  isReadOnly?: boolean;
  onResourceColumnHeaderRename?: (resourceTypeId: string, newName: string) => Promise<void>;
}

export const CostPerUnitForm: FC<CostPerUnitForm> = ({
  estimationCost,
  currencies,
  estimationModel,
  resourceDisplayNames,
  onSubmit,
  isReadOnly,
  onResourceColumnHeaderRename,
}) => {
  const formModelInitial = useMemo<CostPerUnitFormModel>(
    () => ({
      currencyId: estimationCost ? estimationCost.currencyId : estimationModel.currencyIds[0],
      resourceTypeCosts: sortResourceTypes(
        estimationModel.resourceTypes,
        estimationModel.resourceTypeGroups,
      ).map((x) => ({
        resourceTypeId: x.id,
        price:
          estimationCost?.resourceTypeCosts.find(
            (resourceTypeCost) => resourceTypeCost.resourceTypeId === x.id,
          )?.price ??
          x.resourceCost ??
          0,
      })),
    }),
    [estimationCost, estimationModel],
  );

  const onSubmitInner = (formModel: CostPerUnitFormModel) => {
    if (!isEqual(formModelInitial, formModel)) {
      onSubmit(formModel);
    }
  };

  const { control, handleSubmit } = useForm<CostPerUnitFormModel>({
    mode: 'onChange',
    defaultValues: formModelInitial,
  });

  return (
    <form onSubmit={handleSubmit(onSubmitInner)}>
      <CostPerUnitTable
        control={control}
        currencies={currencies}
        estimationModel={estimationModel}
        resourceDisplayNames={resourceDisplayNames}
        onBlur={handleSubmit(onSubmitInner)}
        isReadOnly={isReadOnly}
        onResourceColumnHeaderRename={onResourceColumnHeaderRename}
      />
      {/* The submit button is here, but just hidden so that we can submit form by pressing enter */}
      <input type='submit' className='hidden' />
    </form>
  );
};
